import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./TripAI.css";
import { DateTime } from "luxon";
import { countries } from "../../Data/Countries";
import { overlapError, tripLengthError } from "../../Common/DestinationValidation";

// "Paste Destinations" — extracted from TripAI.js's
// _parsePasteDate/_applyPasteDestinations, same deliberate-extraction
// pattern as ExploreDestinationsFlow.js/ScheduleDestinationsFlow.js. TripAI.js
// keeps its own original inline copy for now — migrating it to use this
// component too is a separate, deliberately deferred follow-up.
//
// Deliberately NOT AI-based (unlike Import Confirmation Email) — plain
// "Place; Country; Start Date; End Date" line parsing, one destination per
// line, applied directly with no review step since there's nothing to
// review beyond what the user already typed.
//
// Props: onApply(destinations), onCancel().

const parsePasteDate = (str) => {
  const s = str.trim();
  const fmtsWithTime = [
    "LLL d yyyy H:mm", "LLL d yyyy HH:mm",
    "LLL dd yyyy H:mm", "LLL dd yyyy HH:mm",
    "LLLL d yyyy H:mm", "LLLL d yyyy HH:mm",
    "d LLL yyyy H:mm", "d LLL yyyy HH:mm",
    "M/d/yyyy H:mm", "M/d/yyyy HH:mm",
    "MM/dd/yyyy H:mm", "MM/dd/yyyy HH:mm",
    "yyyy-M-d H:mm", "yyyy-MM-dd H:mm", "yyyy-MM-dd'T'HH:mm",
    "LLL d yyyy h:mm a", "LLL d yyyy hh:mm a",
    "M/d/yyyy h:mm a", "M/d/yyyy hh:mm a",
  ];
  for (const fmt of fmtsWithTime) {
    const dt = DateTime.fromFormat(s, fmt, { locale: "en-US", zone: "utc" });
    if (dt.isValid) return dt.set({ second: 0 });
  }
  const fmtsDateOnly = [
    "M/d/yyyy", "MM/dd/yyyy", "M/d/yy", "MM/dd/yy",
    "yyyy-M-d", "yyyy-MM-dd",
    "LLL d yyyy", "LLL dd yyyy", "LLLL d yyyy", "LLLL dd yyyy",
    "d LLL yyyy", "dd LLL yyyy", "d LLLL yyyy", "dd LLLL yyyy",
  ];
  for (const fmt of fmtsDateOnly) {
    const dt = DateTime.fromFormat(s, fmt, { locale: "en-US", zone: "utc" });
    if (dt.isValid) return dt.set({ hour: 9, minute: 0, second: 0 });
  }
  return null;
};

const PasteDestinationsFlow = ({ onApply, onCancel }) => {
  const [pasteText, setPasteText] = useState("");
  const [error, setError] = useState("");

  const apply = () => {
    const lines = pasteText.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
    if (lines.length === 0) {
      setError("No destinations entered.");
      return;
    }

    const newDests = [];
    for (let i = 0; i < lines.length; i++) {
      const parts = lines[i].split(";").map((p) => p.trim());
      if (parts.length < 4) {
        setError(`Line ${i + 1}: expected "Place; Country; Start Date; End Date" — got ${parts.length} field${parts.length === 1 ? "" : "s"}.`);
        return;
      }
      const [city, countryRaw, startStr, endStr, typeRaw, labelRaw] = parts;
      // Optional 5th field selects the destination type — defaults to a
      // normal real place. Normalized (lowercased, non-letters stripped) so
      // "No Place", "no-place", "noplace" all match the same way "Skip
      // search"/"skip-search"/"skipsearch" do — matches the two checkbox
      // labels on the destination card (DestinationsScreen.js), just typed
      // instead of clicked. Added 2026-09-15 — see memory:
      // project_waypoint_destination_type_idea.
      const type = (typeRaw || "").toLowerCase().replace(/[^a-z]/g, "");
      const dateStart = parsePasteDate(startStr);
      if (!dateStart) {
        setError(`Line ${i + 1}: can't parse start date "${startStr}". Try Jan 5 2025 or 1/5/2025.`);
        return;
      }
      const dateEnd = parsePasteDate(endStr);
      if (!dateEnd) {
        setError(`Line ${i + 1}: can't parse end date "${endStr}". Try Jan 8 2025 or 1/8/2025.`);
        return;
      }
      if (dateEnd <= dateStart) {
        setError(`Line ${i + 1}: end date must be after start date.`);
        return;
      }
      if (type === "noplace") {
        // A no-place/"Sea Day" destination has no real city — the Place
        // field is reused as its free-text label (stored in `country`, same
        // as toggling "No place" on the card does — see DestinationsScreen.js's
        // withNoPlace). Country field is unused for this type; leaving it
        // blank (two semicolons) is the expected form.
        if (!city) {
          setError(`Line ${i + 1}: "No place" needs a label, e.g. "Sea Day; ; Jul 15 2025; Jul 16 2025; noplace".`);
          return;
        }
        newDests.push({ id: Date.now() + i, city: "", country: city, dateStart, dateEnd });
        continue;
      }
      if (!city) {
        setError(`Line ${i + 1}: place name is empty.`);
        return;
      }
      const matchedCountry = countries.find((c) => c.toLowerCase() === countryRaw.toLowerCase());
      if (!matchedCountry) {
        setError(`Line ${i + 1}: country "${countryRaw}" not recognized. Check spelling.`);
        return;
      }
      if (type === "skipsearch") {
        // A waypoint — real geocoded place, kept off Build My Trip's
        // attraction search. Optional 6th field is its label (e.g. "Hotel",
        // "Fuel Stop"), same as the card's own Label field — falls back to
        // the city name alone when omitted.
        newDests.push({
          id: Date.now() + i,
          city,
          country: matchedCountry,
          dateStart,
          dateEnd,
          noSearch: true,
          ...(labelRaw ? { waypointLabel: labelRaw } : {}),
        });
        continue;
      }
      newDests.push({ id: Date.now() + i, city, country: matchedCountry, dateStart, dateEnd });
    }

    const overlap = overlapError(newDests);
    if (overlap) {
      setError(overlap);
      return;
    }
    const lengthError = tripLengthError(newDests);
    if (lengthError) {
      setError(lengthError);
      return;
    }

    onApply(newDests);
  };

  return (
    <div className="tai-paste-overlay" onClick={onCancel}>
      <div className="tai-paste-modal" onClick={(e) => e.stopPropagation()}>
        <p className="tai-paste-title">Paste Destinations</p>
        <p className="tai-paste-hint">
          One destination per line:&nbsp;
          <span className="tai-paste-example">Place; Country; Start Date; End Date</span>
          <br />e.g. <span className="tai-paste-example">Tokyo; Japan; Jul 12 2025 9:00; Jul 14 2025 18:00</span>
          <br />For a "No place"/"Sea Day" — leave Country blank, add <span className="tai-paste-example">noplace</span>:
          <br /><span className="tai-paste-example">Sea Day; ; Jul 15 2025; Jul 16 2025; noplace</span>
          <br />For "Skip search" — add <span className="tai-paste-example">skipsearch</span>, optionally with a label:
          <br /><span className="tai-paste-example">Nagoya; Japan; Jul 16 2025; Jul 17 2025; skipsearch; Hotel</span>
        </p>
        {error !== "" && <p className="tai-paste-error">{error}</p>}
        <textarea
          className="tai-paste-input"
          placeholder={"Tokyo; Japan; Jul 12 2025 9:00; Jul 14 2025 18:00\nKyoto; Japan; Jul 15 2025 9:00; Jul 18 2025 18:00\nSea Day; ; Jul 18 2025; Jul 19 2025; noplace"}
          value={pasteText}
          onChange={(e) => { setPasteText(e.target.value); setError(""); }}
        />
        <div className="tai-paste-actions">
          <button className="tai-cancel-btn" onClick={onCancel}>Cancel</button>
          <button className="tai-primary-btn" onClick={apply}>
            <FontAwesomeIcon icon={solid("check")} />
            Use These
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasteDestinationsFlow;
